/* global CodeMirror */

const DEFAULT_CODE_MIRROR_CONFIG = {
  lineNumbers: true,
  lineWrapping: true,
  mode: "python",
  theme: "monokai",
};
const DEFAULT_MESSAGES = {
  runButtonLabel: "▶ Run",
  runError: "Unhandled error",
  solutionButtonLabel: "✓ Solution",
  unhandledApiError: "Unhandled API error",
};
const DEFAULT_CONFIG = {
  apiUrlHasTrailingSlash: false,
  codeMirrorConfig: DEFAULT_CODE_MIRROR_CONFIG,
  messages: DEFAULT_MESSAGES,
};

const prepareRequest = (defaultData, request) => {
  const merged = { ...defaultData, ...request };
  if (defaultData && defaultData.headers && request.headers) {
    const mergedHeaders = {};
    defaultData.headers.forEach((value, key) => {
      mergedHeaders[key] = value;
    });
    request.headers.forEach((value, key) => {
      mergedHeaders[key] = value;
    });
    merged.headers = new Headers(mergedHeaders);
  }
  return merged;
};

const apiRequest = ({ config, uri, ...rest }) =>
  fetch(
    new Request(
      buildUrl(config, uri),
      prepareRequest(config.requestConfig, rest)
    )
  ).then((response) =>
    response.text().then((content) => {
      if (!response.ok) {
        try {
          return Promise.reject(new Error(JSON.parse(content).detail));
        } catch {
          return Promise.reject(new Error(config.messages.unhandledApiError));
        }
      }
      return content;
    })
  );

const buildUrl = ({ apiUrlHasTrailingSlash, baseApiUrl }, uri) => {
  const url = `${baseApiUrl}${uri}`;
  return apiUrlHasTrailingSlash ? ensureTrailingSlash(url) : url;
};

const disableButton = (btn) => {
  btn.setAttribute("disabled", "disabled");
};

const enableButton = (btn, cb) => {
  const result = cb();
  btn.removeAttribute("disabled");
  return result;
};

const ensureTrailingSlash = (url) => {
  if (url[url.length - 1] !== "/") return `${url}/`;
  return url;
};

const getCodeMirror = (uid) =>
  document.getElementById(uid).nextSibling.CodeMirror;


const getUidForTaskType = (taskType, name) => taskType ? `${taskType}/${name}` : name

const renderResp = ({className, respMsg = "", uid}) => {
  const elId = `${uid}-error`;
  const maybeEl = document.getElementById(elId);

  const el = maybeEl || document.createElement("div");
  el.className = className;
  if (respMsg instanceof HTMLElement) {
    el.innerHtml = ""
    el.append(respMsg)
  } else {
    el.innerHTML = respMsg.toString();
  }

  if (!maybeEl) {
    el.setAttribute("id", elId);

    const codeTask = document.getElementById(uid);
    if (!codeTask.nextSibling) {
      codeTask.after(el);
    } else {
      codeTask.nextSibling.nextSibling.after(el);
    }
  }
};

const getQueryResultAsTable = data => {
  const columns = Object.keys(data[0])
  const addRow = (getValue, style="") => {
    const tr = document.createElement("tr")
    for (const column of columns) {
      const td = document.createElement("td")
      td.innerText = getValue(column)
      if (style) {
        td.className = style
      }
      tr.append(td)
    }
    table.append(tr)
  }

  const container = document.createElement("div")
  container.className = "sql-query-result-container"

  const table = document.createElement("table")
  table.className = "table"
  container.append(table)

  addRow(column => column, "font-weight-bold")
  for (const row of data) {
    addRow(column => row[column])
  }

  return container
}

const renderSqlResp = (resp, uid) => {
  let queryResultTable;
  let respMsg;

  if (resp.query_result?.length) {
    queryResultTable = getQueryResultAsTable(resp.query_result)
    if (resp.msg) {
      respMsg = document.createElement("div")
      respMsg.append(document.createTextNode(resp.msg))
      respMsg.append(document.createElement("br"))
      respMsg.append(queryResultTable)
    } else {
      respMsg = queryResultTable
    }
  } else  {
    respMsg = resp.msg || "OK"
  }

  renderResp({className: resp.success ? "text-success" : "text-danger", respMsg, uid })
}

const handleLoadSolutionFactory = ({config, name, uid, taskType}) => (evt) => {
  evt.preventDefault();

  const { target: btn } = evt;
  if (btn.getAttribute("disabled")) {
    return;
  }

  disableButton(btn);
  const uri = getUidForTaskType(taskType, name)
  apiRequest({config, uri: `${uri}/solution`})
    .then((content) =>
      enableButton(btn, () => {
        const codeMirror = getCodeMirror(uid);
        codeMirror.setValue(content);
      })
    )
    .catch((err) =>
      enableButton(btn, () => {
        renderResp({className: "text-danger", respMsg: err, uid});
      })
    );
};

const handleRunCodeFactory = ({config, name, uid, taskType}) => (evt) => {
  evt.preventDefault();

  const { target: btn } = evt;
  if (btn.getAttribute("disabled")) {
    return;
  }

  disableButton(btn);
  renderResp({className: "text-danger", uid});

  const uri = getUidForTaskType(taskType, name)

  apiRequest({
    body: getCodeMirror(uid).getValue(),
    config,
    headers: new Headers({
      "Content-Type": "text/x-python",
    }),
    method: "POST",
    uri: uri,
  })
    .then((content) => {
      enableButton(btn, () => {
        const apiData = JSON.parse(content);
        if (taskType === "sql") {
          renderSqlResp(apiData, uid)
          return
        }
        if (apiData.status_code === 0) {
          renderResp({className: "text-success", respMsg: apiData.stdout, uid});
        } else {
          renderResp({className: "text-danger", respMsg: apiData.stderr || config.messages.runError, uid});
        }
      });
    })
    .catch((err) =>
      enableButton(btn, () => {
        renderResp({className: "text-danger", respMsg: err, uid});
      })
    );
};

const initRunButton = (itemBag) => {
  const btn = document.createElement("button");
  btn.className = "btn btn-block btn-success btn-sm rounded-0 text-left pl-4";
  btn.setAttribute("type", "submit");
  btn.innerHTML = itemBag.config.messages.runButtonLabel;
  btn.addEventListener("click", handleRunCodeFactory(itemBag));
  return btn;
};

const initSolutionButton = (itemBag) => {
  const btn = document.createElement("button");
  btn.className =
    "btn btn-block btn-secondary btn-sm rounded-0 text-right pr-4";
  btn.setAttribute("type", "button");
  btn.innerHTML = itemBag.config.messages.solutionButtonLabel;
  btn.addEventListener("click", handleLoadSolutionFactory(itemBag));
  return btn;
};

const initButtonsRow = (uid) => {
  const colRun = document.createElement("div");
  colRun.className = "col-6";
  colRun.appendChild(initRunButton(uid));

  const colSolution = document.createElement("div");
  colSolution.className = "col-6";
  colSolution.appendChild(initSolutionButton(uid));

  const row = document.createElement("div");
  row.className = "row no-gutters";
  row.append(colRun);
  row.append(colSolution);

  return row;
};

const initCodeTaskWidgetFactory = (config) => (item, idx) => {
  const name = item.getAttribute("name");
  const taskType = item.getAttribute("type")
  const uid = `codetask-${name}-${idx}`;
  const itemBag = {config, name, uid, taskType};

  item.setAttribute("id", uid);
  item.dataset.codeTask = true;

  const uri = getUidForTaskType(taskType, name)

  apiRequest({config, uri: uri})
    .then((content) => {
      item.value = content;
      item.after(initButtonsRow(itemBag));
      return CodeMirror.fromTextArea(item, config.codeMirrorConfig);
    })
    .catch((err) => {
      renderResp({className: "text-danger", err, uid});
    });
};

const initCodeTasks = ({
  apiUrlHasTrailingSlash,
  baseApiUrl,
  codeMirrorConfig,
  messages,
  requestConfig,
}) => {
  const config = {
    ...DEFAULT_CONFIG,
    apiUrlHasTrailingSlash,
    baseApiUrl: ensureTrailingSlash(baseApiUrl),
    codeMirrorConfig: {
      ...DEFAULT_CODE_MIRROR_CONFIG,
      ...codeMirrorConfig,
    },
    messages: {
      ...DEFAULT_MESSAGES,
      ...messages,
    },
    requestConfig,
  };
  Array.prototype.slice
    .call(document.querySelectorAll("codetask"))
    .forEach(initCodeTaskWidgetFactory(config));
};

window.initCodeTasks = initCodeTasks;
